/**
 * Fetch the caller's AUTHORITATIVE role from the server.
 *
 * WHY: the ID token's `cognito:groups` is frozen for up to 1h and survives a hard refresh (Cognito
 * keeps it in localStorage), so a user who was demoted — or who demoted themselves — kept seeing
 * Leadership UI. The backend resolves the role from `deliverpro_users` on every request; this call
 * (GET /api/users/manage?self=true — an existing route, no new API resource) returns exactly that
 * resolution so the UI's role gates match what the server will enforce.
 */
import axios from "axios";
import { getApiClient, ACCOUNT_DEACTIVATED } from "@/lib/api";
import type { CurrentUser } from "@/lib/auth";

export type ServerRole = CurrentUser["role"];

export interface ServerCaller {
  email: string;
  name: string;
  role: ServerRole;
  is_active: boolean;
  role_source: "db" | "token-admin" | "token-engineer" | "internal";
}

export type ServerRoleResult =
  | { status: "ok"; caller: ServerCaller }
  /** 403 ACCOUNT_DEACTIVATED — the session must end. */
  | { status: "deactivated" }
  /** Anything else (network, 5xx, 401 after refresh failed) — role could not be verified. */
  | { status: "error" };

const VALID_ROLES: readonly ServerRole[] = [
  "admin",
  "leadership",
  "pm",
  "sa",
  "engineer",
];

export async function fetchServerRole(): Promise<ServerRoleResult> {
  try {
    const response = await getApiClient().get<{ caller?: ServerCaller }>(
      "/api/users/manage?self=true",
      // Background check — never toast. 403/409 handling is done by the caller via the result.
      { _suppressErrorToast: true },
    );
    const caller = response.data?.caller;
    if (!caller || !VALID_ROLES.includes(caller.role)) {
      return { status: "error" };
    }
    return { status: "ok", caller };
  } catch (err) {
    if (
      axios.isAxiosError(err) &&
      err.response?.status === 403 &&
      (err.response.data as { code?: string } | undefined)?.code ===
        ACCOUNT_DEACTIVATED
    ) {
      return { status: "deactivated" };
    }
    return { status: "error" };
  }
}
